import React, { useEffect, useState, useCallback } from 'react';
import { QRCodeCanvas } from "qrcode.react";
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import useStartAttendance from '../../../hooks/useStartAttendance';
import { useSnackbar } from 'notistack';
import { CircularProgress } from '@mui/material';
import { useStore } from '../../../stores/store';
import { useSetMenu } from "../../../hooks/useSetMenu";
import { toMinutes, formatMinutes, timeRange, roomLabel, formatDay } from '../../../utils/lessonLabels';

// The QR a professor shows in class. One token can cover several lessons taught together (a combined
// class); each lesson keeps its own session and count. Everything is read back from the token, so a
// refresh keeps the page. Spec: ops/combined-classes-qr-spec.md
const QRCodeAttendance = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get('token');
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();
  const { openQr } = useStartAttendance();
  const { mainStore } = useStore();
  useSetMenu(mainStore.SchedulePlanMenuType, mainStore.SchedulePlanMenuNo);

  const [status, setStatus] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axiosPrivate.get('/SchedulePlanWeekSessions/SessionStatus', { params: { token } });
      setStatus(response.data);
      setNotFound(false);
    } catch (error) {
      console.error("Error refreshing data:", error);
      if ([401, 403, 404].includes(error?.response?.status)) {
        setNotFound(true);
      }
    }
  }, [token, axiosPrivate]);

  useEffect(() => {
    setStatus(null);
    setNotFound(false);
    refresh();
    const interval = setInterval(refresh, 5000);
    return () => clearInterval(interval);
  }, [refresh]);

  useEffect(() => {
    const ticker = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(ticker);
  }, []);

  const baseUrl = window.__UMS_THEME__?.frontUrl || process.env.REACT_APP_FRONT_URL;
  const qrUrl = `${baseUrl}/attendance/markwithtoken?token=${token}`;

  const lessons = status?.lessons || [];
  const lessonIds = lessons.map(l => l.schedulePlanWeekId);
  const isCombined = lessons.length > 1;
  const sessionType = status?.sessionType || 1;
  const isClosed = !!status && !status.isActive;
  const msLeft = status ? Math.max(0, new Date(status.tokenExpiry).getTime() - now) : 0;
  const isExpired = !!status && !isClosed && msLeft === 0;
  const timeLeft = `${Math.floor(msLeft / 60000)}:${String(Math.floor((msLeft % 60000) / 1000)).padStart(2, '0')}`;

  const scans = (lesson) => (sessionType === 1 ? lesson.startScanCount : lesson.endScanCount) || 0;
  const expectedStudents = lessons.reduce((sum, l) => sum + (l.expected || 0), 0);
  const currentScanCount = lessons.reduce((sum, l) => sum + scans(l), 0);
  const startScanCount = lessons.reduce((sum, l) => sum + (l.startScanCount || 0), 0);
  const endScanCount = lessons.reduce((sum, l) => sum + (l.endScanCount || 0), 0);
  const attendanceRate = expectedStudents > 0 ? (currentScanCount / expectedStudents) * 100 : 0;

  // Date · time · room of the class as a whole, under the title.
  const starts = lessons.map(l => toMinutes(l.startTime)).filter(m => m !== null);
  const ends = lessons.map(l => toMinutes(l.endTime)).filter(m => m !== null);
  const classTime = starts.length && ends.length
    ? `${formatMinutes(Math.min(...starts))}–${formatMinutes(Math.max(...ends))}`
    : '';
  const rooms = [...new Set(lessons.map(l => roomLabel(l) || t("online")))].join(', ');
  const heading = [formatDay(lessons[0]?.weekDay), classTime, rooms].filter(Boolean).join(' · ');

  const handleRecreateSession = async () => {
    if (await openQr(lessonIds, sessionType, { forceNew: true, replace: true })) {
      enqueueSnackbar(t("sessionRecreated"), { variant: "success" });
    }
  };

  // The end QR covers the same lessons as this one.
  const handleOpenEndQr = () => openQr(lessonIds, 2, { replace: true });

  const handleCancelSession = async () => {
    try {
      await axiosPrivate.delete('/SchedulePlanWeekSessions/CancelSessionsByToken', { params: { token } });
      enqueueSnackbar(t("sessionCanceled"), { variant: "success" });
      navigate(-1);
    } catch (error) {
      console.error("Error canceling session:", error);
      enqueueSnackbar(t("errorCancelingSession"), { variant: "error" });
    }
  };

  const viewAttendance = (schedulePlanWeekId) =>
    navigate(`/attendance/studentlist?schedulePlanWeekId=${schedulePlanWeekId}`);

  const backButton = (
    <button className="btn btn-light" onClick={() => navigate(-1)} title={t("backToList")}>
      <i className="bi bi-arrow-left"></i> {t("backToList")}
    </button>
  );

  if (!token || notFound) {
    return (
      <div className="container my-4">
        <div className="alert alert-danger d-flex justify-content-between align-items-center">
          <span>{t("qrNotFound")}</span>
          {backButton}
        </div>
      </div>
    );
  }

  return (
    <div className="container my-4">
      <div className="card">
        <div className="card-header bg-danger text-white d-flex justify-content-between align-items-center gap-2">
          <div>
            <h3 className="mb-0">{sessionType === 1 ? t("scanQRStartSession") : t("scanQREndSession")}</h3>
            <small className="opacity-75">
              {heading || (sessionType === 1 ? t("beginningOfLecture") : t("endOfLecture"))}
            </small>
          </div>
          {backButton}
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-8 text-center">
              {status ? (
                <div className="mb-4">
                  <QRCodeCanvas
                    value={qrUrl}
                    size={600}
                    bgColor={"#ffffff"}
                    fgColor={"#000000"}
                    level={"H"}
                    className="border p-2 rounded"
                    style={{ maxWidth: '100%', height: 'auto', opacity: isClosed || isExpired ? 0.2 : 1 }}
                  />
                  {isClosed ? (
                    <div className="alert alert-secondary mt-3">{t("qrClosed")}</div>
                  ) : isExpired ? (
                    <div className="alert alert-danger mt-3">{t("sessionExpiredMessage")}</div>
                  ) : (
                    <div className="mt-3">
                      <p className="text-muted">
                        {t("sessionExpiresIn")}: <strong>{timeLeft}</strong>
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <CircularProgress sx={{ color: 'var(--brand-red, #dc354d)' }} />
              )}
            </div>
            <div className="col-md-4">
              <div className="card h-100">
                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                  <h4 className="mb-0">{t("attendanceStatistics")}</h4>
                  <span className="badge bg-danger">
                    {sessionType === 1 ? t("startSession") : t("endSession")}
                  </span>
                </div>
                <div className="card-body">
                  {isCombined && (
                    <h6 className="text-muted mb-3">{t("qrSubjectsCovered", { count: lessons.length })}</h6>
                  )}
                  {lessons.map(lesson => {
                    const ownTime = timeRange(lesson.startTime, lesson.endTime);
                    const lessonRate = lesson.expected > 0 ? Math.min(100, (scans(lesson) / lesson.expected) * 100) : 0;
                    return (
                      <div key={lesson.schedulePlanWeekId} className="mb-3">
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div>
                            <strong>{lesson.subjectCode}</strong> {lesson.subjectName}
                            <div className="small text-muted">
                              {[lesson.scheduleType, lesson.groups, ownTime !== classTime ? ownTime : null]
                                .filter(Boolean).join(' · ')}
                            </div>
                          </div>
                          {isCombined && (
                            <strong className="text-danger text-nowrap">
                              {lesson.expected > 0 ? `${scans(lesson)} / ${lesson.expected}` : scans(lesson)}
                            </strong>
                          )}
                        </div>
                        {isCombined && (
                          <>
                            <div className="progress mt-1" style={{ height: '6px' }}>
                              <div className="progress-bar bg-danger" role="progressbar" style={{ width: `${lessonRate}%` }} />
                            </div>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-brand"
                              onClick={() => viewAttendance(lesson.schedulePlanWeekId)}
                            >
                              {t("viewAttendance")}
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}

                  <hr />
                  <div className="d-flex justify-content-between mb-2">
                    <span>{t("totalEnrolled")}:</span>
                    <strong>{expectedStudents}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>{sessionType === 1 ? t("startScan") : t("endScan")}:</span>
                    <strong className="text-danger">{currentScanCount}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>{t("attendanceRate")}:</span>
                    <strong>{attendanceRate.toFixed(1)}%</strong>
                  </div>

                  <div className="progress mb-3" style={{ height: '20px' }}>
                    <div
                      className={`progress-bar ${attendanceRate > 50 ? 'bg-success' : 'bg-warning'}`}
                      role="progressbar"
                      style={{ width: `${Math.min(100, attendanceRate)}%` }}
                      aria-valuenow={attendanceRate}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      {attendanceRate.toFixed(1)}%
                    </div>
                  </div>

                  <hr />
                  <small className="text-muted d-block mb-1">
                    {t("startScan")}: <strong>{startScanCount}</strong>
                  </small>
                  <small className="text-muted d-block">
                    {t("endScan")}: <strong>{endScanCount}</strong>
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="card-footer">
          <div className="d-flex justify-content-between flex-wrap gap-2">
            <button
              className="btn btn-danger"
              onClick={handleCancelSession}
              disabled={!status || isClosed}
            >
              {t("cancelSession")}
            </button>
            <div className="d-flex flex-wrap gap-2">
              {lessons.length === 1 && (
                <button
                  className="btn btn-outline-danger"
                  onClick={() => viewAttendance(lessons[0].schedulePlanWeekId)}
                >
                  {t("viewAttendance")}
                </button>
              )}
              {status && sessionType === 1 && (
                <button className="btn btn-outline-danger" onClick={handleOpenEndQr}>
                  <i className="bi bi-stop-circle me-1"></i>
                  {t("endQr")}
                </button>
              )}
              {(isExpired || isClosed) && (
                <button className="btn btn-danger" onClick={handleRecreateSession}>
                  {t("recreateSession")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeAttendance;
